import { useCallback } from 'react';
import { Editor, Range } from 'slate';
import { useCreateBlockChain } from 'entities/tag';
import { applyBlockTagBinding, collectChainCarriers, bindingsFromResponse } from 'slate/extensions/tag';

export interface ApplyBlockChainArgs {
  editor: Editor;
  range: Range;
  base: {
    documentSuperId: number;
    taxonomyId: string;
    sectionSuperId: string;
    pageId: string;
    conceptId: string;
    lang: string;
    contextId?: string;
    linkroleUri?: string | null;
  };
  conceptTagIds?: Set<string>;
  textCarriersOnly?: boolean;
  predecessorId?: string;
  successorId?: string;
  documentVersionId?: string;
}

export const useApplyBlockChain = () => {
  const { mutateAsync: createBlockChain } = useCreateBlockChain();

  return useCallback(
    async (args: ApplyBlockChainArgs): Promise<{ createdCount: number }> => {
      const { editor, range, base, conceptTagIds, textCarriersOnly, predecessorId, successorId, documentVersionId } = args;
      const carriers = collectChainCarriers(editor, range, { conceptTagIds, textCarriersOnly });
      if (carriers.length === 0) return { createdCount: 0 };

      const created = await createBlockChain({
        ...base,
        carrierElementIds: carriers.map((c) => c.elementId),
        activeSectionSuperId: base.sectionSuperId,
        predecessorId,
        successorId,
        documentVersionId,
      });
      if (!created?.length) return { createdCount: 0 };

      Editor.withoutNormalizing(editor, () => {
        bindingsFromResponse(carriers, created, successorId).forEach(({ path, binding }) => applyBlockTagBinding(editor, path, binding));
      });
      return { createdCount: created.length };
    },
    [createBlockChain]
  );
};
